// components/Survey/SurveyDetailsModal.jsx
import React from 'react';
import { XMarkIcon, DocumentTextIcon, BuildingOfficeIcon, CalendarIcon, UserIcon, FolderIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';

const SurveyDetailsModal = ({ survey, onClose }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusInfo = (survey) => {
    const now = new Date();
    const startDate = survey.startDate ? new Date(survey.startDate) : null;
    const endDate = survey.endDate ? new Date(survey.endDate) : null;

    if (!survey.isActive) {
      return { status: 'Inactive', color: 'text-gray-600', bgColor: 'bg-gray-100' };
    }

    if (startDate && now < startDate) {
      return { status: 'Scheduled', color: 'text-blue-600', bgColor: 'bg-blue-100' };
    }

    if (endDate && now > endDate) {
      return { status: 'Expired', color: 'text-red-600', bgColor: 'bg-red-100' };
    }

    return { status: 'Active', color: 'text-green-600', bgColor: 'bg-green-100' };
  };

  const statusInfo = getStatusInfo(survey);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
              <DocumentTextIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Survey Details</h2>
              <p className="text-gray-500 text-sm">Complete information about the survey</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <DocumentTextIcon className="w-5 h-5 text-purple-500" />
              Basic Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Survey Name</label>
                <p className="text-gray-900 bg-gray-50 rounded-lg p-3">{survey.name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <div className={`inline-flex px-3 py-1 rounded-full text-sm font-semibold ${statusInfo.bgColor} ${statusInfo.color}`}>
                  {statusInfo.status}
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <p className="text-gray-900 bg-gray-50 rounded-lg p-3 min-h-[80px]">
                  {survey.description || 'No description provided'}
                </p>
              </div>
            </div>
          </div>

          {/* Company Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <BuildingOfficeIcon className="w-5 h-5 text-blue-500" />
              Company Information
            </h3>
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-blue-900 font-medium">{survey.company?.name || 'No company assigned'}</p>
              {survey.company?.email && (
                <p className="text-blue-700 text-sm mt-1">{survey.company.email}</p>
              )}
            </div>
          </div>

          {/* Schedule Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-green-500" />
              Schedule
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <div className="flex items-center gap-2 text-gray-900 bg-gray-50 rounded-lg p-3">
                  <CalendarIcon className="w-4 h-4 text-gray-400" />
                  {formatDate(survey.startDate)}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <div className="flex items-center gap-2 text-gray-900 bg-gray-50 rounded-lg p-3">
                  <CalendarIcon className="w-4 h-4 text-gray-400" />
                  {formatDate(survey.endDate)}
                </div>
              </div>
            </div>
          </div>

          {/* Statistics */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <ChatBubbleLeftRightIcon className="w-5 h-5 text-purple-500" />
              Statistics
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center bg-purple-50 rounded-lg p-4">
                <FolderIcon className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-purple-900">{survey.categoryCount || 0}</div>
                <div className="text-sm text-purple-700">Categories</div>
              </div>
              <div className="text-center bg-green-50 rounded-lg p-4">
                <ChatBubbleLeftRightIcon className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-green-900">{survey.responseCount || 0}</div>
                <div className="text-sm text-green-700">Responses</div>
              </div>
              <div className="text-center bg-blue-50 rounded-lg p-4">
                <UserIcon className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-blue-900">
                  {survey.createdByUser ? 'Yes' : 'No'}
                </div>
                <div className="text-sm text-blue-700">Creator</div>
              </div>
              <div className="text-center bg-orange-50 rounded-lg p-4">
                <CalendarIcon className="w-8 h-8 text-orange-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-orange-900">
                  {survey.isActive ? 'Yes' : 'No'}
                </div>
                <div className="text-sm text-orange-700">Active</div>
              </div>
            </div>
          </div>

          {/* Creator Information */}
          {survey.createdByUser && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <UserIcon className="w-5 h-5 text-gray-500" />
                Created By
              </h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-900 font-medium">{survey.createdByUser.userName}</p>
                <p className="text-gray-700 text-sm mt-1">User ID: {survey.createdByUser.id}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SurveyDetailsModal;